import { useEffect, useRef } from 'react';
import { API_URL } from '../config/api';

/**
 * ConversionTracking component
 * 
 * Fires conversion events to Google Ads and Meta Pixel when a booking is completed.
 * Uses backend flag as primary deduplication (works across devices).
 * Falls back to localStorage for same-device deduplication.
 */
const ConversionTracking = ({ 
  booking, 
  organization,
  eventType = 'purchase' // 'purchase', 'initiate_checkout', 'add_to_cart'
}) => {
  const hasFired = useRef(false);
  
  useEffect(() => {
    // Only fire once per component mount
    if (hasFired.current) return;
    if (!booking || !organization) return;
    
    const confirmationCode = booking.confirmationCode || booking.confirmation_code;
    
    // Only fire for purchase events when payment is confirmed
    // Includes all "paid" terminal states
    if (eventType === 'purchase') {
      const paymentStatus = booking.paymentStatus || booking.payment_status;
      const bookingStatus = booking.status;
      const paidStatuses = ['paid', 'partial', 'deposit_paid', 'completed', 'refunded', 'partial_refund'];
      const isPaid = paidStatuses.includes(paymentStatus) || bookingStatus === 'confirmed';
      if (!isPaid) {
        return;
      }
    }
    
    // Fire conversion events — platforms deduplicate server-side by transaction_id.
    // We use hasFired ref to prevent double-fire within the same page mount only.
    const checkAndFireConversion = async () => {
      // Fire conversions
      await fireConversionEvents();
    };
    
    const fireConversionEvents = async () => {
      const value = booking.totalPrice || booking.total_price || booking.amountPaid || booking.amount_paid || 0;
      const currency = organization.currency || 'USD';
      const transactionId = confirmationCode;
      const serviceName = booking.serviceName || booking.service_name || '';
      
      // Google Ads Conversion Tracking
      const googleAdsId = organization.googleAdsId || organization.google_ads_id;
      const googleAdsLabel = organization.googleAdsLabel || organization.google_ads_label;
      
      if (googleAdsId && googleAdsLabel) {
        try {
          // Load gtag if not already loaded
          if (!window.gtag) {
            const script = document.createElement('script');
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${googleAdsId}`;
            document.head.appendChild(script);
            
            window.dataLayer = window.dataLayer || [];
            window.gtag = function() { window.dataLayer.push(arguments); };
            window.gtag('js', new Date());
            window.gtag('config', googleAdsId);
          }
          
          // Fire conversion event
          window.gtag('event', 'conversion', {
            'send_to': `${googleAdsId}/${googleAdsLabel}`,
            'value': value,
            'currency': currency,
            'transaction_id': transactionId
          });
        } catch (error) {
          console.error('[Conversion] Google Ads error:', error);
        }
      }

      // GA4 Tracking — fires to the operator's own GA4 property
      const ga4Id = organization.ga4MeasurementId || organization.google_analytics_id;

      if (ga4Id) {
        try {
          window.dataLayer = window.dataLayer || [];
          if (!window.gtag) {
            window.gtag = function() { window.dataLayer.push(arguments); };
          }
          if (!window[`_ga4Loaded_${ga4Id}`]) {
            const script = document.createElement('script');
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${ga4Id}`;
            document.head.appendChild(script);
            window.gtag('js', new Date());
            // No debug_mode — events should appear in standard GA4 reports
            window.gtag('config', ga4Id);
            window[`_ga4Loaded_${ga4Id}`] = true;
          }
          window.gtag('event', 'purchase', {
            transaction_id: transactionId,
            value: value,
            currency: currency,
            send_to: ga4Id,
            items: [{
              item_id: booking.serviceId || booking.service_id || transactionId,
              item_name: serviceName,
              price: value,
              quantity: booking.partySize || booking.party_size || 1,
            }],
          });
        } catch (error) {
          console.error('[Conversion] GA4 error:', error);
        }
      }
      
      // Meta Pixel Tracking
      const metaPixelId = organization.metaPixelId || organization.meta_pixel_id;
      
      if (metaPixelId) {
        try {
          // Load Meta Pixel if not already loaded
          if (!window.fbq) {
            window.fbq = function() {
              window.fbq.callMethod ? 
                window.fbq.callMethod.apply(window.fbq, arguments) : 
                window.fbq.queue.push(arguments);
            };
            window.fbq.push = window.fbq;
            window.fbq.loaded = true;
            window.fbq.version = '2.0';
            window.fbq.queue = [];
            
            const script = document.createElement('script');
            script.async = true;
            script.src = 'https://connect.facebook.net/en_US/fbevents.js';
            document.head.appendChild(script);
            
            window.fbq('init', metaPixelId);
          }
          
          // Fire purchase event — serviceName is now correctly defined above
          window.fbq('track', 'Purchase', {
            value: value,
            currency: currency,
            content_name: serviceName,
            content_type: 'service',
            content_ids: [transactionId]
          });
        } catch (error) {
          console.error('[Conversion] Meta Pixel error:', error);
        }
      }
      
      // Mark as tracked
      hasFired.current = true;
      
      // Save to localStorage (backup)
      const conversionKey = `conversion_tracked_${confirmationCode}`;
      localStorage.setItem(conversionKey, Date.now().toString());
      
      // Mark in backend (primary deduplication for cross-device)
      try {
        await fetch(`${API_URL}/api/v2/public/bookings/${confirmationCode}/mark-conversion`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ source: 'ticket_page' })
        });
      } catch (err) {
        // Silent fail - localStorage is backup
      }
    };
    
    checkAndFireConversion();
    
  }, [booking, organization, eventType]);
  
  // This component doesn't render anything visible
  return null;
};

export default ConversionTracking;
